package dev.lingvo.site.pages.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LArrowRightIcon
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LDivider
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LFilterBar
import dev.lingvo.site.ds.LFilterField
import dev.lingvo.site.ds.LIconAction
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LPageState
import dev.lingvo.site.ds.LPagination
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.WorkspaceRequestKind
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** How many rows one page of the log shows. The server sends up to 100; nobody scrolls 100. */
private const val ACTIVITY_PAGE_SIZE = 25

/**
 * Everything that happened, across every project.
 *
 * The "see all" behind the overview's eight rows, and deliberately the same sentences from the same
 * builder rather than a second rendering — two pages describing the same event differently is how
 * somebody ends up unsure which one to believe.
 */
@Page("/app/activity")
@Layout(".components.layouts.AppLayout")
@Composable
fun ActivityPage() {
    val ctx = rememberPageContext()
    var refreshKey by remember { mutableStateOf(0) }
    val context = WorkspaceRequestContext.current()
    // Both filters and the page cursor are keyed to the workspace: switching workspaces while a
    // project filter is applied would otherwise carry a slug that does not exist in the new one.
    var projectFilter by remember(context?.key) { mutableStateOf("") }
    // The second axis §5 asks for. Held as the stored wire constant rather than the label, because
    // the label is translated and the constant is the contract `activityLabel` switches on.
    var kindFilter by remember(context?.key) { mutableStateOf("") }
    var page by remember(context?.key) { mutableStateOf(0) }
    val decision = context?.decision(WorkspaceRequestKind.ACTIVITY)
    val reloadable = if (context != null && decision?.allowed == true) {
        loadReloadable(context.key, reloadToken = refreshKey) {
            ApiClient.activity(context.workspace.slug).also { entries ->
                check(context.acceptsActivity(entries)) {
                    "The server returned activity from a different workspace."
                }
            }
        }
    } else {
        null
    }
    val state = when {
        context == null -> Async.Success(emptyList())
        decision?.allowed != true -> Async.Failure(decision?.refusal.orEmpty())
        else -> requireNotNull(reloadable).state
    }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.activity.activity", "Activity"),
            t("app.activity.what_everyone_has_been_doing_newest", "What everyone has been doing, newest first."),
        ) {
            if (reloadable != null) {
                LIconAction(
                    label = t("app.activity.refresh_this_log", "Refresh this log"),
                    onClick = { refreshKey++ },
                    loading = reloadable.refreshing,
                    enabled = !reloadable.refreshing,
                ) { LRefreshIcon() }
            }
        }
        reloadable?.refreshError?.let { failure ->
            // `refreshError` is only ever set by a re-fetch over an already-visible success, which
            // is to say by the reader pressing Retry. Without `live` the outcome of their own
            // action was announced to nobody — the overview renders the same construct with it.
            LAlert(
                t("app.activity.the_refresh_did_not_replace_this", "The refresh did not replace this workspace"),
                failure.message,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }

        LAsyncContent(
            state,
            rows = 8,
            loadingLabel = t("app.activity.loading_activity", "Loading activity"),
            // A workspace on an older server refuses this request before it is sent. That is a
            // version mismatch rather than a fault, so it is stated as one — a Retry button here
            // would refuse identically every time it was pressed.
            failure = { failure ->
                if (decision?.allowed == false) {
                    LAlert(t("app.activity.this_workspace_needs_a_newer_server", "This workspace needs a newer server"), failure.message, variant = LAlertVariant.INFO)
                } else {
                    AsyncFailureState(failure, onRetry = { refreshKey++ })
                }
            },
        ) { entries ->
            if (entries.isEmpty()) {
                // Both empties end in a button. The slot was passed `{}` in each of them, so the
                // page that opens on a brand-new account said "join or create a workspace" and
                // offered nothing to press — while the overview answers the identical state with
                // an action.
                if (context == null) {
                    LEmptyState(
                        t("app.activity.no_workspace_selected", "No workspace selected"),
                        t("app.activity.a_workspace_holds_the_projects_this", "A workspace holds the projects this log is about. Create one, or ask " +
                            "whoever invited you to send the invitation again."),
                        Modifier.fillMaxWidth(),
                        // A log of what happened is history, not a refresh.
                        LEmptyStateIcon.AUDIT,
                    ) {
                        LButton(
                            t("app.activity.create_a_workspace", "Create a workspace"),
                            { ctx.router.tryRoutingTo("/app/workspaces") },
                        )
                    }
                } else {
                    LEmptyState(
                        t("app.activity.nothing_yet", "Nothing yet"),
                        t("app.activity.import_a_file_or_write_a", "Import a file or write a translation and it will show up here."),
                        Modifier.fillMaxWidth(),
                        LEmptyStateIcon.REFRESH,
                    ) {
                        LButton(
                            t("app.activity.see_all_projects", "See all projects"),
                            { ctx.router.tryRoutingTo("/app/projects") },
                            variant = LButtonVariant.SECONDARY,
                        )
                    }
                }
            } else {
                // **The filter and the page live over the fetched list, and say so.**
                // `/overview/activity` takes a `limit` and no cursor, so this page has no claim to
                // make about where a row sits in the whole sequence once it has filtered — which is
                // exactly what `LPageState(pageSize = 0)` exists to express. Told otherwise, the
                // pagination sentence would read "Events 51 to 53" about a set the server never
                // paged.
                val projectSlugs = entries.mapNotNull { it.projectSlug }.distinct().sorted()
                // Only the kinds this log actually contains: a select listing every constant the
                // product can write would offer twelve filters that all match nothing.
                val kinds = entries.mapNotNull { it.type }.distinct().sortedBy(::activityLabel)
                val visible = entries
                    .filter { projectFilter.isBlank() || it.projectSlug == projectFilter }
                    .filter { kindFilter.isBlank() || it.type == kindFilter }
                val pageCount = ((visible.size + ACTIVITY_PAGE_SIZE - 1) / ACTIVITY_PAGE_SIZE)
                    .coerceAtLeast(1)
                val currentPage = page.coerceIn(0, pageCount - 1)
                val shown = visible.drop(currentPage * ACTIVITY_PAGE_SIZE).take(ACTIVITY_PAGE_SIZE)

                // Present whenever there is more than one of anything to narrow by. The bar used
                // to be gated on two *projects*, so the single-project workspace — which is every
                // new one — got a bare list of near-identical rows, no way to filter by kind, and
                // not even the "5 of 25" line that says how much is off-screen. It is still absent
                // when neither axis has a choice in it: a filter bar with no filters in it is a
                // control that does nothing.
                if (projectSlugs.size > 1 || kinds.size > 1) {
                    LFilterBar(
                        fields = buildList {
                            if (projectSlugs.size > 1) {
                                add(
                                    LFilterField(
                                        id = "project",
                                        label = t("app.activity.project", "Project"),
                                        activeValue = projectFilter
                                            .takeIf(String::isNotBlank)
                                            ?.let { slug -> ProjectDirectory.find(slug)?.name ?: slug },
                                        onClear = { projectFilter = ""; page = 0 },
                                    ) { fieldModifier ->
                                        LSelect(
                                            value = projectFilter,
                                            options = listOf(
                                                LSelectOption("", t("app.activity.every_project", "Every project")),
                                            ) + projectSlugs.map { slug ->
                                                LSelectOption(slug, ProjectDirectory.find(slug)?.name ?: slug)
                                            },
                                            onValueChange = { projectFilter = it; page = 0 },
                                            modifier = fieldModifier,
                                            label = t("app.activity.project", "Project"),
                                        )
                                    },
                                )
                            }
                            if (kinds.size > 1) {
                                add(
                                    LFilterField(
                                        id = "kind",
                                        label = t("app.activity.event", "Event"),
                                        activeValue = kindFilter
                                            .takeIf(String::isNotBlank)
                                            ?.let(::activityLabel),
                                        onClear = { kindFilter = ""; page = 0 },
                                    ) { fieldModifier ->
                                        LSelect(
                                            value = kindFilter,
                                            options = listOf(
                                                LSelectOption("", t("app.activity.every_kind_of_event", "Every kind of event")),
                                            ) + kinds.map { kind -> LSelectOption(kind, activityLabel(kind)) },
                                            onValueChange = { kindFilter = it; page = 0 },
                                            modifier = fieldModifier,
                                            label = t("app.activity.event", "Event"),
                                        )
                                    },
                                )
                            }
                        },
                        onClearAll = { projectFilter = ""; kindFilter = ""; page = 0 },
                        modifier = Modifier.fillMaxWidth(),
                        label = t("app.activity.filter_this_log", "Filter this log"),
                        resultSummary = tf(
                            "app.activity.showing_of_events",
                            "{shown} of {total}",
                            "shown" to formatCount(visible.size),
                            "total" to formatCount(entries.size),
                        ),
                    )
                }

                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth()) {
                        shown.forEachIndexed { index, entry ->
                            Row(
                                Modifier.fillMaxWidth().gap(Space.MD),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                // The same mark the overview's work log carries for the same rows,
                                // so the "see all" is visibly the longer version of the eight above
                                // it rather than a second, differently-drawn account — and now one
                                // mark per kind of event rather than the same history glyph
                                // twenty-five times, which is what makes this column scannable at
                                // the length only this page reaches.
                                ActivityGlyph(entry.type)
                                Column(Modifier.flexGrow(1).gap(Space.XXS)) {
                                    Span(Modifier.fontSize(TypeScale.BodySmall).toAttrs()) {
                                        Text(entry.sentence)
                                    }
                                    // The same age the overview prints for the same event. This
                                    // page promises the same sentences from the same builder and
                                    // then stamped them `2026-09-02 14:03:11 UTC` while the
                                    // overview said "3 h ago"; the exact instant is still here,
                                    // one hover away, rather than being the default reading.
                                    LMuted(
                                        formatRelativeTime(entry.at),
                                        Modifier.attr("title", formatUtc(entry.at, LTimeStyle.STAMP)),
                                    )
                                }
                                // One per entry; the sentence that distinguishes them is a
                                // sibling, so the name has to carry it.
                                //
                                // A ghost link button rather than a bare `Link`: bare, it inherited
                                // the user agent's `blue`/`aqua` — a second accent, in a CSS
                                // keyword — and carried no mark, which every other row action in
                                // the release does.
                                LLinkButton(
                                    label = t("app.activity.open", "Open"),
                                    path = entry.href,
                                    variant = LButtonVariant.GHOST,
                                    size = LButtonSize.SM,
                                    leadingIcon = { LArrowRightIcon() },
                                    accessibleLabel = trf(
                                        "app.activity.open_entry",
                                        "Open {entry}",
                                        "entry" to entry.sentence,
                                    ),
                                )
                            }
                            if (index != shown.lastIndex) LDivider()
                        }
                        if (pageCount > 1) {
                            LPagination(
                                state = LPageState(
                                    shown = shown.size,
                                    // 0 — this page filters after the fetch, so it cannot honestly
                                    // say which rows of the whole log these are.
                                    pageSize = 0,
                                    hasOlder = currentPage < pageCount - 1,
                                    hasNewer = currentPage > 0,
                                    pageNumber = currentPage + 1,
                                ),
                                onOlder = { page = currentPage + 1 },
                                onNewer = { page = currentPage - 1 },
                                unit = t("app.activity.events", "events"),
                            )
                        }
                    }
                }
            }
        }
    }
}
